import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Empty } from './ui';

export interface BaseConnection { id: string; name: string; enabled: boolean }

/**
 * List + selection + save/delete for one connection type. All three protocol tabs
 * share the same CRUD shape, so they share this hook and differ only in their form.
 */
export function useConnections<T extends BaseConnection>(basePath: string) {
  const [items, setItems] = useState<T[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (preferId?: string) => {
    try {
      const list = await api.get<T[]>(basePath);
      setItems(list);
      setSelectedId((current) => preferId ?? (current && list.some((i) => i.id === current) ? current : list[0]?.id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load connections');
      setItems([]);
    }
  }, [basePath]);

  useEffect(() => { void load(); }, [load]);

  const create = useCallback(async (name: string) => {
    setBusy(true);
    setError('');
    try {
      const created = await api.post<T>(basePath, { name });
      await load(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create connection');
    } finally {
      setBusy(false);
    }
  }, [basePath, load]);

  const save = useCallback(async (id: string, patch: Partial<T>) => {
    setBusy(true);
    setError('');
    try {
      await api.patch<T>(`${basePath}/${id}`, patch);
      await load(id);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save connection');
      return false;
    } finally {
      setBusy(false);
    }
  }, [basePath, load]);

  const remove = useCallback(async (id: string) => {
    setBusy(true);
    try {
      await api.del(`${basePath}/${id}`);
      setSelectedId(null);
      await load();
    } finally {
      setBusy(false);
    }
  }, [basePath, load]);

  const selected = items?.find((i) => i.id === selectedId) ?? null;
  return { items, selected, selectedId, setSelectedId, load, create, save, remove, error, setError, busy };
}

export function ConnectionPicker<T extends BaseConnection>({ items, selectedId, onSelect, onCreate, busy, newLabel }: {
  items: T[]; selectedId: string | null; onSelect: (id: string) => void;
  onCreate: (name: string) => void; busy: boolean; newLabel: string;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');

  return (
    <div className="card" style={{ padding: '12px 14px' }}>
      <div className="row">
        {items.map((c) => (
          <button
            key={c.id}
            className={c.id === selectedId ? 'primary' : ''}
            onClick={() => onSelect(c.id)}
            title={c.enabled ? undefined : 'Disabled'}
          >
            {c.name}{!c.enabled && ' ·'}
          </button>
        ))}
        <div className="spacer" />
        {adding ? (
          <>
            <input
              autoFocus value={name} placeholder={newLabel} style={{ width: 200 }}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) { onCreate(name.trim()); setName(''); setAdding(false); }
                if (e.key === 'Escape') setAdding(false);
              }}
            />
            <button
              className="primary" disabled={busy || !name.trim()}
              onClick={() => { onCreate(name.trim()); setName(''); setAdding(false); }}
            >Add</button>
            <button className="ghost" onClick={() => setAdding(false)}>Cancel</button>
          </>
        ) : (
          <button onClick={() => setAdding(true)}>+ New</button>
        )}
      </div>
    </div>
  );
}

export function NoConnections({ what, onCreate, busy }: {
  what: string; onCreate: (name: string) => void; busy: boolean;
}) {
  return (
    <Empty>
      <p style={{ marginTop: 0 }}>No {what} yet.</p>
      <button className="primary" disabled={busy} onClick={() => onCreate(`New ${what}`)}>
        Create the first one
      </button>
    </Empty>
  );
}
